import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button, EmptyState, IconButton, Spinner, Tabs } from "@/ui";
import { useEngineQuery } from "@/engine/hooks";
import { Page } from "@/app/layout/Page";
import { ContactNeedsOffersStrip, ContactNetworkSection } from "@/features/network";
import { ConnectionsSection } from "@/features/map/ConnectionsSection";
import { ContactHeader } from "./ContactHeader";
import { useContactActions } from "./ContactActionHost";
import { CadenceSummary, CustomFieldsBlock, GroupsSection, Highlights, MergedRecords, NotesSection, ProfileFields, ProfileHistory, RelatedPeople, RemindersSection, SourcesSection, TimelineSection } from "./ContactSections";
import s from "../shared.module.css";

type Tab = "overview" | "timeline" | "notes" | "reminders" | "network" | "sources";

export function ContactPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as Tab | null) ?? "overview";
  const detail = useEngineQuery("getContact", { contactId: id }, { enabled: !!id });
  const actions = useContactActions(id || null, { afterArchive: () => navigate("/people") });
  const [seenKey, setSeenKey] = useState<string | null>(null);
  useEffect(() => {
    if (detail.data && seenKey !== id) setSeenKey(id);
  }, [detail.data, id, seenKey]);

  if (detail.isLoading) return <div style={{ display: "flex", justifyContent: "center", padding: 60 }}><Spinner /></div>;
  if (!detail.data) {
    return (
      <EmptyState title="This person is not here" action={<Button onClick={() => navigate("/people")}>Back to people</Button>}>
        They may have been merged into someone else or deleted.
      </EmptyState>
    );
  }
  const d = detail.data;
  return (
    <Page
      narrow
      before={
        <IconButton label="Back" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} />
        </IconButton>
      }
      title={<span className="u-grow" />}
    >
      <ContactHeader detail={d} onAction={actions.open} />
      <div style={{ marginTop: 16 }}>
        <Tabs<Tab>
          value={tab}
          onChange={(t) => setParams((p) => { const n = new URLSearchParams(p); if (t === "overview") n.delete("tab"); else n.set("tab", t); return n; }, { replace: true })}
          tabs={[
            { id: "overview", label: "Overview" },
            { id: "timeline", label: `Timeline${d.statistics ? ` (${d.statistics.emailCount + d.statistics.eventCount + d.statistics.messageCount + d.statistics.callCount})` : ""}` },
            { id: "notes", label: `Notes${d.notes.length ? ` (${d.notes.length})` : ""}` },
            { id: "reminders", label: `Reminders${d.reminders.filter((r) => !r.isProcessed).length ? ` (${d.reminders.filter((r) => !r.isProcessed).length})` : ""}` },
            { id: "network", label: `Needs and offers${d.needs.length + d.services.length ? ` (${d.needs.length + d.services.length})` : ""}` },
            { id: "sources", label: `Sources (${d.lookups.length})` },
          ]}
        />
      </div>
      <div style={{ paddingTop: 16 }}>
        {tab === "overview" && (
          <>
            <Highlights detail={d} />
            <ContactNeedsOffersStrip detail={d} />
            <ProfileFields detail={d} />
            <CustomFieldsBlock detail={d} />
            <div className={s.cardGrid} style={{ gridTemplateColumns: "1fr 1fr" }}>
              <RelatedPeople detail={d} />
              <div>
                <GroupsSection detail={d} onAdd={() => actions.open("group")} />
                {!d.contact.isMemorialized && <CadenceSummary detail={d} onEdit={() => actions.open("cadence")} />}
              </div>
            </div>
            <ConnectionsSection contactId={d.contact.id} />
            {d.notes.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <NotesSection contactId={d.contact.id} notes={d.notes.slice(0, 3)} />
              </div>
            )}
            <ProfileHistory contactId={d.contact.id} />
          </>
        )}
        {tab === "timeline" && <TimelineSection contactId={d.contact.id} />}
        {tab === "notes" && <NotesSection contactId={d.contact.id} notes={d.notes} />}
        {tab === "reminders" && <RemindersSection contactId={d.contact.id} reminders={d.reminders} />}
        {tab === "network" && <ContactNetworkSection contactId={d.contact.id} />}
        {tab === "sources" && (
          <>
            <SourcesSection detail={d} />
            <MergedRecords contactId={d.contact.id} />
          </>
        )}
      </div>
      {actions.modals}
    </Page>
  );
}
